import { BrowserRouter, Routes, Route } from 'react-router';
import { createContext, useReducer, useRef, useEffect, useState } from 'react';

import Layout from './components/Layout';
import Alert from './components/alert/Alert';
import Dashboard from './components/dashboard/Dashboard';
import ChartPage from './components/charts/ChartPage';
import ProtectedRoute from './ProtectedRoute';
import Login from './components/login/Login';
import ActivityDialog from './components/ActivityDialog';
import { addCalculatedColumns } from './components/custom';
import { getServerURL } from './utils';

const SERVER_URL = getServerURL();
const MOUSEMOVE = 'mousemove';
const ACTIVITY_TIMEOUT = 1;
const REEVALUATE_TIMEOUT = 28;

const minutesToMilliseconds = (minutes) => {
  return minutes * 60 * 1000;
};

const initialState = {
  alertData: [],
  headerTitle: '',
  user: {
    name: {
      first: null,
      last: null,
    },
    email: null,
    phoneNumber: null,
    userType: null,
    organization: null,
    chatID: null,
    isAuthenticated: false,
  },
  chartData: {
    isFetched: false,
    content: [],
  },
  token: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'login': {
      return {
        ...state,
        user: action.user,
        alertData: action.alertData,
        token: action.token,
      };
    }
    case 'logout': {
      // Want to keep headerTitle
      return {
        ...state,
        user: initialState.user,
        alertData: initialState.alertData,
        chartData: initialState.chartData,
        token: initialState.token,
      };
    }
    case 'set-page-title': {
      return {
        ...state,
        headerTitle: action.title,
      };
    }
    case 'set-alert-data': {
      const { newAlert } = action.payload;
      return {
        ...state,
        alertData: [newAlert, ...state.alertData],
      };
    }
    case 'update-alert-status': {
      const {
        alertID,
        historyEntry,
        nextAlertStatus,
        shouldRemoveFromDashboard = null,
        unclaimedClinics,
        // Tree only set by socket
        // unclaim status updates.
        isTreeOnDashboard = true,
      } = action.payload;
      const updatedAlertData = state.alertData.map((entry) => {
        if (entry.alertID === alertID) {
          if (shouldRemoveFromDashboard) return null;
          const updatedData = {
            ...entry,
            alertStatus: nextAlertStatus,
            actionHistory: {
              actions: [...entry.actionHistory.actions, historyEntry],
            },
            unclaimedClinics,
            // Leave tree as is unless explicitly
            // passed as false.
            isTreeOnDashboard: !isTreeOnDashboard
              ? false
              : entry.isTreeOnDashboard,
          };
          const withCCs = addCalculatedColumns(
            updatedData,
            state.user.organization,
          );
          return withCCs;
        }
        return entry;
      });
      return {
        ...state,
        alertData: shouldRemoveFromDashboard
          ? updatedAlertData.filter((entry) => entry !== null)
          : updatedAlertData,
      };
    }
    case 'update-discharge-date': {
      const { alertID, historyEntry, updatedDischargeDate } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              dischargeDate: updatedDischargeDate,
              actionHistory: {
                actions: [...entry.actionHistory.actions, historyEntry],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case 'change-appointment-date': {
      const { alertID, historyEntry, changedAppointmentDate } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              appointmentDate: changedAppointmentDate,
              actionHistory: {
                actions: [...entry.actionHistory.actions, historyEntry],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case 'update-alert-status-and-appointment': {
      const {
        alertID,
        historyEntry,
        alertStatus,
        appointmentDate,
        historyEntryAppointmentDate,
      } = action.payload;
      return {
        ...state,
        alertData: state.alertData.map((entry) => {
          if (entry.alertID === alertID) {
            const updatedData = {
              ...entry,
              appointmentDate,
              alertStatus,
              actionHistory: {
                actions: [
                  ...entry.actionHistory.actions,
                  historyEntry,
                  historyEntryAppointmentDate,
                ],
              },
            };
            const withCCs = addCalculatedColumns(
              updatedData,
              state.user.organization,
            );
            return withCCs;
          }
          return entry;
        }),
      };
    }
    case 'set-chart-data': {
      return {
        ...state,
        chartData: {
          isFetched: true,
          content: action.payload.chartData,
        },
      };
    }
    case 'set-chart-data-fetched': {
      return {
        ...state,
        chartData: {
          ...state.chartData,
          isFetched: true,
        },
      };
    }
    case 'update-access-token': {
      return {
        ...state,
        token: action.payload.accessToken,
      };
    }
    default: {
      throw new Error(
        `State reducer function called with an unknown type: ${action.type}`,
      );
    }
  }
};

export const ClinicRxContext = createContext(null);

export default function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const lastActivityRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);

  const autoAttemptRefresh = async () => {
    const refreshResult = await fetch(`${SERVER_URL}/refresh-token`, {
      credentials: 'include',
    });
    if (!refreshResult.ok) {
      // Open the dialog on error
      // to try again.
      // If that fails, the activity
      // dialog component has a
      // something went wrong
      // snackbar.
      setIsActivityDialogOpen(true);
      return;
    }
    const { token } = await refreshResult.json();
    dispatch({
      type: 'update-access-token',
      payload: { accessToken: token },
    });
    activityCheck();
  };

  const activityCheck = () => {
    timeoutIdRef.current = setTimeout(() => {
      if (
        !lastActivityRef.current ||
        Date.now() - lastActivityRef.current >
          minutesToMilliseconds(ACTIVITY_TIMEOUT)
      ) {
        setIsActivityDialogOpen(true);
      } else {
        autoAttemptRefresh();
      }
    }, minutesToMilliseconds(REEVALUATE_TIMEOUT));
  };

  useEffect(() => {
    const trackActivity = () => {
      lastActivityRef.current = Date.now();
    };
    document.addEventListener(MOUSEMOVE, trackActivity);
    return () => {
      document.removeEventListener(MOUSEMOVE, trackActivity);
      clearTimeout(timeoutIdRef.current);
    };
  }, []);

  const fetchProtected = async (fetchFn) => {
    // No try / catch because
    // callers handle throws.
    const response = await fetchFn(`Bearer ${state.token}`);
    if (response.status !== 401) {
      return response;
    }
    const refreshResult = await fetch(`${SERVER_URL}/refresh-token`, {
      credentials: 'include',
    });
    if (!refreshResult.ok) {
      dispatch({ type: 'logout' });
      return response;
    }
    const { token } = await refreshResult.json();
    dispatch({
      type: 'update-access-token',
      payload: { accessToken: token },
    });
    activityCheck();
    return await fetchFn(`Bearer ${token}`);
  };

  return (
    <ClinicRxContext.Provider value={{ state, dispatch, fetchProtected }}>
      <>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route
                path='/'
                element={<Login checkActivity={activityCheck} />}
              />
              <Route element={<ProtectedRoute />}>
                <Route
                  path='/alert'
                  element={<Alert />}
                />
                <Route
                  path='/dashboard'
                  element={<Dashboard />}
                />
                <Route
                  path='/charts'
                  element={<ChartPage />}
                />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        {isActivityDialogOpen && (
          <ActivityDialog
            isOpen={isActivityDialogOpen}
            handleClose={() => setIsActivityDialogOpen(false)}
            checkActivity={activityCheck}
          />
        )}
      </>
    </ClinicRxContext.Provider>
  );
}
